import { computed, Ref, ComputedRef } from "vue";
import { useI18n } from "vue-i18n";
import { isEmpty } from "@utils/var.check";
import { ResourceContext } from "@plugins/richtext/types.ts";
import { type QuestionLayout, QUESTION_LAYOUT } from "@/types/question";

interface Question {
  parentQuestionUuid?: string;
  layout?: QuestionLayout;
  [key: string]: string | object | undefined;
}

interface QuestionViewOptions {
  question?: Question;
  questionLayout?: Ref<QuestionLayout | undefined> | ComputedRef<QuestionLayout | undefined>;
  resourceContext?: ResourceContext;
  isFileSubmissionQuestion?: Ref<boolean>;
  isShortAnswerQuestion?: Ref<boolean>;
}

export function useQuestionView(options: QuestionViewOptions) {
  const { t } = useI18n();

  const isTeamResponseQuestion = computed(() => {
    return options.resourceContext?.teamResponse();
  });

  const isInMpq = computed(() => {
    return !isEmpty(options.question?.parentQuestionUuid);
  });

  const isQuestionInMpq = isInMpq;

  const isQuestionInDisplay = computed(() => {
    return options.resourceContext?.isQuestionInDisplay ?? false;
  });

  const alignTwoColumns = computed(() => {
    const layout = options.questionLayout?.value ?? options.question?.layout;
    return (
      layout === QUESTION_LAYOUT.SIDE_BY_SIDE &&
      !isQuestionInMpq.value &&
      !isQuestionInDisplay.value
    );
  });

  const isInQuiz = computed(() => {
    return options.resourceContext?.isInQuiz;
  });

  const instructionContent = computed(() => {
    if (options.isFileSubmissionQuestion?.value && isTeamResponseQuestion.value) {
      return t("question_component_file_and_team_response_instructions_content");
    } else if (options.isFileSubmissionQuestion?.value) {
      if (options.isShortAnswerQuestion?.value) {
        return t("question_component_sa_fa_file_instructions_content");
      } else {
        return t("question_component_label_file_instructions_content");
      }
    } else if (isTeamResponseQuestion.value) {
      return t("question_component_team_response_instructions_content");
    }
    return undefined;
  });

  return {
    isTeamResponseQuestion,
    isInMpq,
    isQuestionInMpq,
    alignTwoColumns,
    isQuestionInDisplay,
    isInQuiz,
    instructionContent,
  };
}
